import PropTypes from 'prop-types';
import React from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import { HorizontalDiv } from '../../../../../components/Common';
import { CANCEL, SAVE, UNIFORMITY_VALUES } from '../../../../../res/Constants';
import themeButton from '../../../../../res/styles/theme_button.module.scss';
import { dismissToast, toastError } from './../../../../../ui/ToastHelper';
import EditableReportItem from './EditableReportItem';

DialogEditing.propTypes = {
    isOpen: PropTypes.bool,
    reportData: PropTypes.object,
    linesCount: PropTypes.number,
    cacType: PropTypes.number,
    contractHarvest: PropTypes.string,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    isNewReport: PropTypes.bool,
    isAdmin: PropTypes.bool
};

DialogEditing.defaultProps = {
    isOpen: false,
    onSave: () => { },
    onCancel: () => { },
    isNewReport: false,
    isAdmin: false
};

export default function DialogEditing(props) {
    const [currentReportData, setCurrentReportData] = React.useState(null);
    const [allFieldsValid, setAllFieldsValid] = React.useState(true);
    const [reportUser, setReportUser] = React.useState(null);

    const onReportEdited = (data, allFieldsValid, userPk) => {
        setCurrentReportData(data);
        setAllFieldsValid(allFieldsValid);
        setReportUser(userPk);
    };

    const onSave = React.useCallback(() => {
        if (!allFieldsValid) {
            return toastError('Invalid values');
        }
        dismissToast();
        const sanitizedData = sanitizeUniformity(currentReportData);
        // Pass reportUser as separate parameter
        props.onSave(sanitizedData, reportUser);
    }, [props.onSave, currentReportData, allFieldsValid, reportUser]);

    const sanitizedReportData = sanitizeReportValues(props.reportData, '-');

    // Create a unique key based on report data to force remount when data changes
    const reportKey = React.useMemo(() => {
        if (props.reportData?.pk) {
            return `report-${props.reportData.pk}`;
        }
        // For new reports, create a key based on the data content
        const dataString = JSON.stringify({
            standA: props.reportData?.standA,
            boltA: props.reportData?.boltA,
            remarksA: props.reportData?.remarksA,
            roguingA: props.reportData?.roguingA,
            expYield: props.reportData?.expYield
        });
        return `new-report-${dataString}`;
    }, [props.reportData]);

    return (
        <Modal isOpen={props.isOpen}>
            <ModalBody>
                {sanitizedReportData != null ? (
                    <EditableReportItem
                        key={reportKey}
                        linesCount={props.linesCount}
                        reportData={sanitizedReportData}
                        cacType={props.cacType}
                        contractHarvest={props.contractHarvest}
                        onReportEdited={onReportEdited}
                        isNewReport={props.isNewReport}
                        isAdmin={props.isAdmin}
                    />
                ) : (
                    <p>Loading...</p>
                )}
            </ModalBody>
            <ModalFooter>
                <StyledFooterDiv>
                    <button className={themeButton.green} onClick={onSave}>
                        {SAVE.toUpperCase()}
                    </button>
                    <button className={themeButton.red} onClick={props.onCancel}>
                        {CANCEL.toUpperCase()}
                    </button>
                </StyledFooterDiv>
            </ModalFooter>
        </Modal>
    );
}

function sanitizeReportValues(obj, value) {
    const safeObjCopy = { ...obj };
    Object.entries(safeObjCopy).forEach(([k, v]) => {
        if (v === value) {
            safeObjCopy[k] = '';
        }
    });
    return safeObjCopy;
}

function sanitizeUniformity(currentReportData) {
    const reportData = { ...currentReportData };
    const uniformityA = reportData.lineA?.Uniformity;
    if (uniformityA === 'Yes' || uniformityA === 'No') {
        reportData.lineA.Uniformity = UNIFORMITY_VALUES[uniformityA];
    }
    const uniformityB = reportData.lineB?.Uniformity;
    if (uniformityB === 'Yes' || uniformityB === 'No') {
        reportData.lineB.Uniformity = UNIFORMITY_VALUES[uniformityB];
    }
    return reportData;
}

const StyledFooterDiv = styled(HorizontalDiv)`
    width: 100%;
    justify-content: space-between;

    button {
        width: 150px;
    }
`;
